// packages/kb-view/src/citation-link.ts
//
// LAT-2183 — the one citation-link grammar for answer text
// (`[label](path#Lstart-Lend)`, with `ref:` / `uuid:` Knowledge Base
// qualifiers). ADR 0049 §1 / LAT-1840: enforcing a grammar twice from two
// regexes is enforcing two grammars, and the divergence is silent. This
// module is the single definition; `apps/web` and `apps/mcp` import it.
//
// This is a React-free entry point (`@atmina/kb-view/citation-link`). It
// imports `./source-span.js` and nothing else — the package boundary test
// pins that. `safePercentDecode` is inlined rather than imported from
// `./kb-link-target` so the import set stays that one relative path.
//
// The grammar governs ANSWER TEXT on a chat or channel surface, never a KB
// file body. File-body links are ADR 0038 / `resolveKbLinkPath`, which has
// no `:` rule; a qualified target written into a body mints an unresolvable
// edge.

import {
  parseSourceLineRangeFragment,
  type SourceLineRange,
} from "./source-span.js";

/** An inclusive, 1-based line range harvested from a span fragment. */
export type { SourceLineRange };

export type CitationLinkQualifier =
  | { kind: "uuid"; kbId: string }
  | { kind: "ref"; teamSlug: string; kbSlug: string };

export type CitationLinkTarget =
  | { kind: "external"; href: string }
  | {
      kind: "path";
      path: string;
      qualifier: CitationLinkQualifier | null;
      fragment: string | null;
      lines: SourceLineRange | null;
    };

export type CitationLink = {
  label: string;
  raw: string;
  start: number;
  end: number;
  target: CitationLinkTarget;
};

const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;
const UUID_QUALIFIER =
  /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}):(.*)$/i;
const REF_QUALIFIER =
  /^([a-z0-9]+(?:-[a-z0-9]+)*)\/([a-z0-9]+(?:-[a-z0-9]+)*):(.*)$/;
const PATH_ALPHABET = /^[A-Za-z0-9._/-]+$/;

// Code alternatives come first so a `[label](target)` inside a fence, an
// unterminated (streaming) fence, or an inline span is consumed as code and
// never seen by the link branch. Same fence classes as the in-app linkifier.
const CODE_OR_LINK = new RegExp(
  [
    "```[\\s\\S]*?```",
    "~~~[\\s\\S]*?~~~",
    "```[\\s\\S]*$",
    "~~~[\\s\\S]*$",
    "`+[^`\\n]*`+",
    "\\[([^\\[\\]\\n]+)\\]\\(([^)\\n]+)\\)",
  ].join("|"),
  "g",
);

/**
 * Percent-decode exactly once, safely: a malformed `%` sequence stays a
 * literal `%` — never a throw. Same rule as `./kb-link-target`.
 */
function safePercentDecode(s: string): string {
  let out = "";
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (ch === "%" && /^[0-9a-fA-F]{2}$/.test(s.slice(i + 1, i + 3))) {
      out += String.fromCharCode(parseInt(s.slice(i + 1, i + 3), 16));
      i += 2;
    } else {
      out += ch;
    }
  }
  return out;
}

function parsePathTarget(
  raw: string,
  qualifier: CitationLinkQualifier | null,
): CitationLinkTarget | null {
  const hash = raw.indexOf("#");
  const pathPart = hash === -1 ? raw : raw.slice(0, hash);
  const fragment = hash === -1 || hash === raw.length - 1 ? null : raw.slice(hash + 1);
  if (pathPart === "") return null;
  const path = safePercentDecode(pathPart);
  if (!PATH_ALPHABET.test(path)) return null;
  const lines = fragment ? parseSourceLineRangeFragment(fragment) : null;
  return { kind: "path", path, qualifier, fragment, lines };
}

/**
 * Classify one link destination. Parse order is fixed (RULED): UUID
 * qualifier, then ref qualifier, then external scheme/`//`/`#`, then an
 * unqualified path. Reversing UUID and scheme turns every KB whose id
 * starts a–f into a dead external anchor (`SCHEME_RE` accepts `f847db41:`).
 *
 * Returns `null` for forms the grammar does not match: a CommonMark title,
 * an angle-bracket destination, whitespace, or a path outside the alphabet
 * after one percent-decode.
 */
export function parseCitationLinkTarget(dest: string): CitationLinkTarget | null {
  if (dest === "" || dest.startsWith("<") || /\s/.test(dest)) return null;

  const uuidMatch = UUID_QUALIFIER.exec(dest);
  if (uuidMatch) {
    return parsePathTarget(uuidMatch[2]!, {
      kind: "uuid",
      kbId: uuidMatch[1]!.toLowerCase(),
    });
  }

  const refMatch = REF_QUALIFIER.exec(dest);
  if (refMatch) {
    return parsePathTarget(refMatch[3]!, {
      kind: "ref",
      teamSlug: refMatch[1]!,
      kbSlug: refMatch[2]!,
    });
  }

  if (SCHEME_RE.test(dest) || dest.startsWith("//") || dest.startsWith("#")) {
    return { kind: "external", href: dest };
  }

  return parsePathTarget(dest, null);
}

function summarise(link: CitationLink): unknown {
  if (link.target.kind === "external") {
    return { label: link.label, kind: "external", href: link.target.href };
  }
  return {
    label: link.label,
    kind: "path",
    path: link.target.path,
    qualifier: link.target.qualifier,
    fragment: link.target.fragment,
    lines: link.target.lines,
  };
}

/**
 * Harvest every citation link in answer text. External links are emitted
 * as `{ kind: "external" }` so consumers leave them untouched. Forms the
 * grammar does not match are omitted — they render as their plain label
 * (or as ordinary markdown the sanitiser already handles).
 */
export function findCitationLinks(text: string): CitationLink[] {
  const links: CitationLink[] = [];
  CODE_OR_LINK.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = CODE_OR_LINK.exec(text)) !== null) {
    const label = match[1];
    const dest = match[2];
    if (label === undefined || dest === undefined) continue;
    const target = parseCitationLinkTarget(dest);
    if (target === null) continue;
    links.push({
      label,
      raw: match[0],
      start: match.index,
      end: match.index + match[0].length,
      target,
    });
  }
  return links;
}

/** Fixture-shaped projection so vector suites compare without offsets. */
export function citationLinkVectorShape(link: CitationLink): unknown {
  return summarise(link);
}
